import React, { useState } from 'react';
import { 
  Brain, 
  Volume2, 
  Mic, 
  Star,
  Award,
  Filter,
  BarChart3,
  Zap,
  Shield,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  Radar, 
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  Cell
} from 'recharts';

const ProviderComparison = ({ stats, loading }) => {
  const [selectedService, setSelectedService] = useState('all');
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [comparisonMetric, setComparisonMetric] = useState('overall');

  const COLORS = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#06B6D4', '#84CC16'];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse h-96"></div>
          <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse h-96"></div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse h-96"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Нет данных для отображения</p>
      </div>
    );
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 2,
    }).format(value);
  };

  // Filter data based on selected service
  const allProviders = stats.providers || [];
  const filteredProviders = allProviders.filter(provider => 
    selectedService === 'all' || provider.service === selectedService
  );

  // Get unique providers for selection
  const uniqueProviders = [...new Set(filteredProviders.map(p => p.name))];

  // Filter selected providers
  const comparisonData = filteredProviders.filter(provider => 
    selectedProviders.length === 0 || selectedProviders.includes(provider.name)
  );

  // Radar chart data
  const radarData = stats.radarComparison || [];
  const performanceMetrics = stats.performanceMetrics || [];
  const costEfficiencyData = stats.costEfficiency || [];
  const reliabilityData = stats.reliability || [];

  // Get service icon
  const getServiceIcon = (service) => {
    switch (service.toLowerCase()) {
      case 'llm':
        return Brain;
      case 'tts':
        return Volume2;
      case 'stt':
        return Mic;
      default:
        return Brain;
    }
  };

  // Get rating stars
  const getRatingStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />);
    }
    
    if (hasHalfStar) {
      stars.push(<Star key="half" className="w-4 h-4 fill-yellow-200 text-yellow-400" />);
    }
    
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="w-4 h-4 text-gray-300" />);
    }
    
    return stars;
  };

  // Get status icon
  const getStatusIcon = (status) => {
    return status === 'active' ? (
      <CheckCircle className="w-5 h-5 text-green-500" />
    ) : (
      <XCircle className="w-5 h-5 text-red-500" />
    );
  };

  const handleProviderToggle = (providerName) => {
    setSelectedProviders(prev => 
      prev.includes(providerName)
        ? prev.filter(p => p !== providerName)
        : [...prev, providerName]
    );
  };

  const selectTopProviders = () => {
    const topProviders = uniqueProviders.slice(0, 3);
    setSelectedProviders(topProviders);
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <select
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Все сервисы</option>
              <option value="llm">LLM</option>
              <option value="tts">TTS</option>
              <option value="stt">STT</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-gray-500" />
            <select
              value={comparisonMetric}
              onChange={(e) => setComparisonMetric(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="overall">Общая оценка</option>
              <option value="performance">Производительность</option>
              <option value="cost">Стоимость</option>
              <option value="reliability">Надежность</option>
              <option value="quality">Качество</option>
            </select>
          </div>
        </div>

        <button
          onClick={selectTopProviders}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Выбрать топ-3
        </button>
      </div>

      {/* Provider Selection */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Выберите провайдеров для сравнения</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {uniqueProviders.map((providerName) => {
            const provider = filteredProviders.find(p => p.name === providerName);
            const isSelected = selectedProviders.includes(providerName);
            
            return (
              <div
                key={providerName}
                onClick={() => handleProviderToggle(providerName)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  isSelected 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {provider && (
                      <>
                        {React.createElement(getServiceIcon(provider.service), {
                          className: 'w-5 h-5 text-blue-600'
                        })}
                        <span className="font-medium text-gray-900">{providerName}</span>
                      </>
                    )}
                  </div>
                  {getStatusIcon(provider?.status)}
                </div>
                <div className="text-sm text-gray-600 mb-2">{provider?.service}</div>
                <div className="flex items-center gap-1">
                  {provider?.rating && getRatingStars(provider.rating)}
                  <span className="text-sm text-gray-600 ml-1">({provider.rating})</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Comparison Charts */}
      {selectedProviders.length > 0 && (
        <>
          {/* Radar Chart */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Сравнительная диаграмма</h3>
            <ResponsiveContainer width="100%" height={400}>
              <RadarChart data={radarData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="metric" />
                <PolarRadiusAxis angle={90} domain={[0, 100]} />
                {selectedProviders.map((provider, index) => (
                  <Radar
                    key={provider}
                    name={provider}
                    dataKey={provider}
                    stroke={COLORS[index % COLORS.length]}
                    fill={COLORS[index % COLORS.length]}
                    fillOpacity={0.1}
                    strokeWidth={2}
                  />
                ))}
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Performance Metrics */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Метрики производительности</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={performanceMetrics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="metric" />
                <YAxis />
                <Tooltip />
                <Legend />
                {selectedProviders.map((provider, index) => (
                  <Bar
                    key={provider}
                    dataKey={provider}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Cost Efficiency Scatter Plot */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Эффективность затрат</h3>
            <ResponsiveContainer width="100%" height={300}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="cost" 
                  name="Стоимость"
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <YAxis 
                  dataKey="performance" 
                  name="Производительность"
                />
                <Tooltip 
                  formatter={(value, name) => [
                    name === 'cost' ? formatCurrency(value) : value,
                    name === 'cost' ? 'Стоимость' : 'Производительность'
                  ]}
                />
                <Scatter name="Провайдеры" data={costEfficiencyData}>
                  {costEfficiencyData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={COLORS[uniqueProviders.indexOf(entry.provider) % COLORS.length]} 
                    />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>

          {/* Reliability Trend */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Надежность сервисов</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={reliabilityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })}
                />
                <YAxis domain={[0, 100]} />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleDateString('ru-RU')}
                  formatter={(value) => [`${value}%`, 'Надежность']}
                />
                <Legend />
                {selectedProviders.map((provider, index) => (
                  <Line
                    key={provider}
                    type="monotone"
                    dataKey={provider}
                    stroke={COLORS[index % COLORS.length]}
                    strokeWidth={2}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {/* Detailed Comparison Table */}
      {selectedProviders.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Детальное сравнение</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Провайдер
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Сервис
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Рейтинг
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Стоимость запроса
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Время ответа
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Надежность
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Качество
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Статус
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {comparisonData.map((provider, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {React.createElement(getServiceIcon(provider.service), {
                          className: 'w-5 h-5 text-blue-600'
                        })}
                        <span className="font-medium text-gray-900">{provider.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{provider.service}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {getRatingStars(provider.rating)}
                        <span className="text-sm text-gray-600 ml-1">({provider.rating})</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-900">{formatCurrency(provider.costPerRequest)}</td>
                    <td className="px-6 py-4 text-gray-900">{provider.avgResponseTime}мс</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{provider.reliability}%</span>
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full" 
                            style={{ width: `${provider.reliability}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{provider.qualityScore}/10</span>
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${(provider.qualityScore / 10) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusIcon(provider.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recommendations */}
      {selectedProviders.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Рекомендации</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <Award className="w-12 h-12 text-yellow-500 mx-auto mb-2" />
              <h4 className="font-semibold text-gray-900 mb-1">Лучший по цене</h4>
              <p className="text-gray-600">{stats.recommendations?.bestPrice || 'N/A'}</p>
            </div>
            <div className="text-center">
              <Zap className="w-12 h-12 text-blue-500 mx-auto mb-2" />
              <h4 className="font-semibold text-gray-900 mb-1">Самый быстрый</h4>
              <p className="text-gray-600">{stats.recommendations?.fastest || 'N/A'}</p>
            </div>
            <div className="text-center">
              <Shield className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <h4 className="font-semibold text-gray-900 mb-1">Самый надежный</h4>
              <p className="text-gray-600">{stats.recommendations?.mostReliable || 'N/A'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderComparison;
